// shell/components/table/columns/column-base.ts

import { IDisposable } from '../../types';
import { HeatMapShader, HeatMapBounds } from '../../heat-map';
import { ColumnDef } from '../types';

/**
 * Data passed to column renderers for cell building.
 * Generic T is required for type-safe item access.
 */
export interface CellRenderContext<T> {
    /** Column definition */
    column: ColumnDef<T>;
    /** Row item */
    item: T;
    /** Extracted cell value (already wrapped in try-catch by caller) */
    value: unknown;
    /** Row index */
    rowIndex: number;
    /** Heat map bounds (if enabled) */
    heatMapBounds?: HeatMapBounds;
    /** Computed shade rank for heat map */
    shadeRank?: number;
}

/**
 * Interface for rendering error states.
 * Implemented by ColumnRendererFactory, can be passed to renderers if needed.
 */
export interface CellErrorRenderer {
    renderError(message?: string): HTMLElement;
}

/**
 * Update types for partial re-rendering.
 */
export enum CellUpdateType {
    /** Only update shading */
    Shades,
    /** Full content update (value changed or row recycled to new item) */
    Full
}

/**
 * Result of build/update operations.
 * Returns new content element to allow parent to track it.
 */
export interface CellRenderResult {
    /** The content element (may be new if replaced during update) */
    contentElement: HTMLElement | null;
}

/**
 * Abstract base class for column renderers.
 *
 * Each column type extends this and implements:
 * - buildGridCell: Create DOM for grid mode
 * - buildTableCell: Create DOM for table mode
 * - updateGridCell: Update for grid mode (handles recycling)
 * - updateTableCell: Update for table mode (handles recycling)
 *
 * Renderers must implement IDisposable to clean up event listeners
 * and observers created during cell rendering.
 *
 * IMPORTANT: The HeatMapShader instance is injected by ColumnRendererFactory
 * and shared across all renderers for efficiency.
 */
export abstract class ColumnRenderer<T = unknown> implements IDisposable {
    /** Whether this column type supports grid mode */
    abstract readonly supportsGrid: boolean;

    /** Whether this column type supports table mode */
    abstract readonly supportsTable: boolean;

    /** Shared HeatMapShader instance (injected by factory) */
    protected heatMapShader!: HeatMapShader;

    /** Track cleanup callbacks for elements */
    protected readonly cleanupCallbacks = new WeakMap<HTMLElement, () => void>();

    /** Cache for frequently queried child elements */
    protected readonly elementCache = new WeakMap<HTMLElement, Map<string, HTMLElement>>();

    /**
     * Set the shared HeatMapShader instance.
     * Called by ColumnRendererFactory after construction.
     */
    setHeatMapShader(shader: HeatMapShader): void {
        this.heatMapShader = shader;
    }

    /** CSS class to add to grid cells (BEM: cs-column-{type}) */
    abstract getGridCellClass(): string;

    /** CSS class to add to table cells (BEM: cs-column-{type}) */
    abstract getTableCellClass(): string;

    /**
     * Build cell DOM for grid mode.
     * @returns Inner content element (not the cell wrapper), or null if nothing to render
     */
    abstract buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement | null;

    /**
     * Build cell DOM for table mode.
     * @param cell - The cell wrapper (can be HTMLElement for CSS Grid or HTMLTableCellElement)
     * @returns Inner content element (not the cell), or null if nothing to render
     */
    abstract buildTableCell(ctx: CellRenderContext<T>, cell: HTMLElement): HTMLElement | null;

    /**
     * Update existing grid cell. MUST handle row recycling (new item bound to same cell).
     * @returns Result with potentially new content element
     */
    abstract updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult;

    /**
     * Update existing table cell. MUST handle row recycling (new item bound to same cell).
     * @param cell - The cell wrapper (can be HTMLElement for CSS Grid or HTMLTableCellElement)
     * @returns Result with potentially new content element
     */
    abstract updateTableCell(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult;

    /**
     * Clean up resources for a specific element.
     * Called when element is recycled or removed.
     */
    cleanupElement(element: HTMLElement): void {
        const cleanup = this.cleanupCallbacks.get(element);
        if (cleanup) {
            try {
                cleanup();
            } catch (e) {
                console.error('Cleanup error:', e);
            }
            this.cleanupCallbacks.delete(element);
        }
        this.elementCache.delete(element);
    }

    /**
     * Register cleanup callback for an element.
     * Multiple callbacks are chained with try-finally for safety.
     */
    protected registerCleanup(element: HTMLElement, cleanup: () => void): void {
        const existing = this.cleanupCallbacks.get(element);
        if (existing) {
            // Combine with existing cleanup - use try-finally to ensure both run
            this.cleanupCallbacks.set(element, () => {
                try {
                    existing();
                } finally {
                    cleanup();
                }
            });
        } else {
            this.cleanupCallbacks.set(element, cleanup);
        }
    }

    /**
     * Cache a child element for efficient update queries.
     */
    protected cacheElement(parent: HTMLElement, key: string, child: HTMLElement): void {
        let cache = this.elementCache.get(parent);
        if (!cache) {
            cache = new Map();
            this.elementCache.set(parent, cache);
        }
        cache.set(key, child);
    }

    /**
     * Get a cached child element.
     */
    protected getCachedElement(parent: HTMLElement, key: string): HTMLElement | undefined {
        return this.elementCache.get(parent)?.get(key);
    }

    /**
     * Apply heat map shading to an element.
     * Delegates to HeatMapShader for color computation.
     */
    protected applyShade(el: HTMLElement, shadeRank: number | undefined, bounds: HeatMapBounds | undefined): void {
        if (shadeRank != null && bounds) {
            this.heatMapShader.applyToElement(el, shadeRank, bounds);
        } else {
            this.heatMapShader.clearFromElement(el);
        }
    }

    /**
     * Format value for display.
     * Handles common types: string, number, Date, boolean, null.
     */
    protected formatValue(value: unknown, converter?: (v: unknown, item: T) => string, item?: T): string {
        if (converter) {
            try {
                return converter(value, item as T);
            } catch (e) {
                console.error('Column converter error:', e);
                return '?';
            }
        }

        if (value == null || value === '') {
            return '-';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'number') {
            return this.formatNumber(value);
        }

        if (value instanceof Date) {
            return this.formatDate(value);
        }

        if (typeof value === 'boolean') {
            return value ? 'yes' : 'no';
        }

        return String(value);
    }

    /**
     * Format number with thousand separators.
     */
    protected formatNumber(value: number): string {
        return value.toLocaleString();
    }

    /**
     * Format date intelligently based on age.
     */
    protected formatDate(date: Date): string {
        const now = new Date();

        // Compare calendar dates, not timestamps (handles timezone correctly)
        const isToday = date.toDateString() === now.toDateString();

        if (isToday) {
            return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        }

        // Check if same year
        if (date.getFullYear() === now.getFullYear()) {
            return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }

        // Older: show full date
        return date.toLocaleDateString();
    }

    /**
     * Create a span element with text content.
     */
    protected createTextSpan(text: string, className?: string): HTMLSpanElement {
        const span = document.createElement('span');
        if (className) span.className = className;
        span.textContent = text;
        return span;
    }

    /**
     * Build an error placeholder element.
     * Public to allow ColumnRendererFactory to use it for error boundaries.
     */
    buildErrorPlaceholder(message: string = 'Error'): HTMLElement {
        const span = document.createElement('span');
        span.className = 'cs-column-error';
        span.textContent = message;
        span.title = message;
        return span;
    }

    /**
     * Dispose all managed resources.
     * Subclasses should call super.dispose() and clean up their own resources.
     */
    dispose(): void {
        // WeakMap entries are automatically cleaned up when elements are GC'd
        // Subclasses override for additional cleanup
    }
}
